// Month-level commission waiver / adjustment for one supplier (spec §5). Lives in the
// Collection & Invoicing panel above the monthly invoice card: shows base → effective revenue
// and opens a small modal to waive the whole month, apply a signed amount with a reason, or
// clear. Never touches per-order terms or invoices — the BE applies it in the calc layer and
// prints it as its own line on the monthly invoice.
import { ReconciliationSupplierDetail, useUpdateCommissionAdjustmentApi } from 'apis/reconciliation';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useCustomToast,
  useDisclosure,
} from 'design-system';
import { numOnly } from 'pages/inquiry-processor/utils/num-only';
import { formatAed } from 'pages/reconciliation/utils/format';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

export interface ICommissionAdjustmentCard {
  month: string;
  detail: ReconciliationSupplierDetail;
}

type Mode = 'none' | 'waive' | 'discount' | 'increase';

export const CommissionAdjustmentCard = ({ month, detail }: ICommissionAdjustmentCard) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();
  const modal = useDisclosure();
  const adjustment = detail.commissionAdjustment ?? null;
  const isClosed = detail.status === 'closed';
  const baseRevenue = adjustment?.baseRevenue ?? detail.revenue;

  const [mode, setMode] = useState<Mode>('none');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!modal.isOpen) return;
    if (adjustment?.waived) setMode('waive');
    else if (adjustment?.amount != null) setMode(adjustment.amount < 0 ? 'discount' : 'increase');
    else setMode('none');
    setAmount(adjustment?.amount != null ? String(Math.abs(adjustment.amount)) : '');
    setReason(adjustment?.reason ?? '');
  }, [modal.isOpen]);

  const { mutate: updateAdjustment, isLoading } = useUpdateCommissionAdjustmentApi({
    onSuccess: () => {
      toast({ description: 'Commission adjustment saved — revenue and invoice amount updated', status: 'success' });
      queryClient.invalidateQueries(['reconciliation-supplier-details', month, detail.supplierId]);
      queryClient.invalidateQueries(['reconciliation-suppliers']);
      queryClient.invalidateQueries(['reconciliation-summary', month]);
      modal.onClose();
    },
  });

  const numeric = amount.trim() === '' ? null : Number(amount);
  const needsAmount = mode === 'discount' || mode === 'increase';
  const amountError =
    !needsAmount || numeric === null
      ? null
      : Number.isNaN(numeric) || numeric <= 0
      ? 'Enter an amount greater than 0'
      : !/^\d*(\.\d{0,2})?$/.test(amount)
      ? 'Use at most 2 decimal places'
      : null;
  const needsReason = mode !== 'none';
  const canSubmit = !isLoading && !amountError && (!needsAmount || numeric !== null) && (!needsReason || reason.trim());

  const signedAmount = needsAmount && numeric !== null ? (mode === 'discount' ? -numeric : numeric) : null;
  const previewRevenue =
    mode === 'waive' ? 0 : Math.max(Math.round((baseRevenue + (signedAmount ?? 0)) * 100) / 100, 0);

  const submit = () => {
    if (!canSubmit) return;
    updateAdjustment({
      month,
      supplierId: detail.supplierId,
      waived: mode === 'waive',
      adjustment: mode === 'discount' || mode === 'increase' ? signedAmount : null,
      reason: needsReason ? reason.trim() : null,
    });
  };

  const meta = adjustment
    ? [
        adjustment.adjustedByName ? `by ${adjustment.adjustedByName}` : null,
        adjustment.adjustedAt ? dayjs(adjustment.adjustedAt).format('DD MMM YYYY') : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <Stack spacing='xs' border='1px solid' borderColor='gray.100' borderRadius='md' p='sm' bg='white'>
      <HStack justify='space-between'>
        <Text fontSize='xs' fontWeight='semibold' color='gray.500' textTransform='uppercase' letterSpacing='0.4px'>
          Commission
        </Text>
        {!isClosed && (
          <Button size='xs' variant='link' onClick={modal.onOpen}>
            {adjustment ? 'Edit adjustment' : 'Waive / adjust'}
          </Button>
        )}
      </HStack>

      {adjustment ? (
        <Stack spacing='2px'>
          <HStack justify='space-between'>
            <Text fontSize='sm' color='gray.500'>
              Per-order commissions
            </Text>
            <Text fontSize='sm' color='gray.500' whiteSpace='nowrap'>
              {formatAed(adjustment.baseRevenue)}
            </Text>
          </HStack>
          <HStack justify='space-between' align='flex-start'>
            <Tooltip label={adjustment.reason ?? ''} placement='top' hasArrow isDisabled={!adjustment.reason}>
              <Text
                fontSize='sm'
                color={adjustment.waived ? '#c77c00' : 'gray.500'}
                borderBottom='1px dotted'
                borderColor='gray.300'
                as='span'>
                {adjustment.waived
                  ? 'Waived'
                  : adjustment.amount != null && adjustment.amount < 0
                  ? 'Discount'
                  : 'Adjustment'}
                {adjustment.reason ? ` — ${adjustment.reason}` : ''}
              </Text>
            </Tooltip>
            <Text fontSize='sm' color={adjustment.waived ? '#c77c00' : 'gray.700'} whiteSpace='nowrap'>
              {adjustment.waived
                ? `−${formatAed(adjustment.baseRevenue)}`
                : `${(adjustment.amount ?? 0) < 0 ? '−' : '+'}${formatAed(Math.abs(adjustment.amount ?? 0))}`}
            </Text>
          </HStack>
          <HStack justify='space-between' borderTop='1px solid' borderColor='gray.100' pt='2px'>
            <Text fontSize='sm' fontWeight='bold' color='gray.700'>
              Revenue
            </Text>
            <Text fontSize='sm' fontWeight='bold' color='gray.700' whiteSpace='nowrap'>
              {formatAed(detail.revenue)}
            </Text>
          </HStack>
          {meta && (
            <Text fontSize='xs' color='gray.400'>
              {meta}
            </Text>
          )}
        </Stack>
      ) : (
        <Text fontSize='sm' color='gray.500'>
          Revenue {formatAed(detail.revenue)} — no waiver or adjustment for this month.
        </Text>
      )}

      <Modal
        isOpen={modal.isOpen}
        onClose={modal.onClose}
        size={{ base: 'full', md: 'md' }}
        closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Commission — {detail.supplierName}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing='md'>
              <Text fontSize='sm' color='gray.600'>
                Applies to this supplier for the month only. Per-order commissions and their invoices are not changed;
                the adjustment appears as its own line on the monthly invoice.
              </Text>
              <RadioGroup value={mode} onChange={(value) => setMode(value as Mode)}>
                <Stack spacing='xs'>
                  <Radio value='none' colorScheme='red'>
                    <Text fontSize='sm'>No adjustment</Text>
                  </Radio>
                  <Radio value='waive' colorScheme='red'>
                    <Text fontSize='sm'>Waive all commission for this month</Text>
                  </Radio>
                  <Radio value='discount' colorScheme='red'>
                    <Text fontSize='sm'>Reduce commission by an amount</Text>
                  </Radio>
                  <Radio value='increase' colorScheme='red'>
                    <Text fontSize='sm'>Add an extra amount</Text>
                  </Radio>
                </Stack>
              </RadioGroup>

              {needsAmount && (
                <FormControl isInvalid={!!amountError} isRequired>
                  <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                    Amount (AED)
                  </FormLabel>
                  <Input size='sm' maxW='200px' value={amount} onChange={(e) => setAmount(numOnly(e.target.value))} />
                  <FormHelperText fontSize='xs'>{amountError ?? 'Before VAT.'}</FormHelperText>
                </FormControl>
              )}

              {needsReason && (
                <FormControl isRequired>
                  <FormLabel fontSize='sm' fontWeight='semibold' color='gray.600' mb='6px'>
                    Reason
                  </FormLabel>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} />
                </FormControl>
              )}

              <Box px='sm' py='xs' bg='#FAFBFC' borderRadius='md'>
                <Text fontSize='sm' color='gray.600'>
                  Per-order commissions <strong>{formatAed(baseRevenue)}</strong> → revenue{' '}
                  <strong>{formatAed(previewRevenue)}</strong> AED
                </Text>
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing='sm'>
              <Button variant='outline' onClick={modal.onClose} isDisabled={isLoading}>
                Cancel
              </Button>
              <Button colorScheme='red' onClick={submit} isDisabled={!canSubmit} isLoading={isLoading}>
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
